import { languageManager, SUPPORTED_LANGUAGES, type Language } from './languageManager';

/**
 * Strip language prefix from path if present
 */
export const stripLanguagePrefix = (path: string): string => {
    const segments = path.split('/').filter(Boolean);
    if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0] as Language)) {
        return '/' + segments.slice(1).join('/') || '/';
    }
    return path;
};

/**
 * Get language prefix from path if present
 */
export const getLanguageFromPath = (path: string): Language | null => {
    const segments = path.split('/').filter(Boolean);
    if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0] as Language)) {
        return segments[0] as Language;
    }
    return null;
};

/**
 * Add language prefix to path
 */
export const addLanguagePrefix = (path: string, lang: Language): string => {
    const stripped = stripLanguagePrefix(path);
    if (stripped === '/') {
        return `/${lang}`;
    }
    return `/${lang}${stripped}`;
};

/**
 * Client-side routing helper to navigate without page refresh
 * Automatically prepends current language to internal routes
 * @param href - The URL to navigate to
 */
export const navigateTo = (href: string) => {
    setTimeout(() => {
        if (href.startsWith('/')) {
            // Internal route - prepend language prefix if not already present
            const lang = languageManager.getLanguage();
            const existingLang = getLanguageFromPath(href);

            let finalHref = href;
            if (!existingLang) {
                // No language in URL, add current language
                finalHref = addLanguagePrefix(href, lang);
            }

            // Use pushState for SPA navigation
            window.history.pushState({}, '', finalHref);
            // Sync languageManager from new URL
            languageManager.syncFromUrl();
            // Trigger a popstate event to update the UI
            window.dispatchEvent(new PopStateEvent('popstate'));
        } else {
            // External URL - use normal navigation
            window.location.href = href;
        }
    }, 10);
};

/**
 * Handle click events for anchor tags to prevent default and use SPA routing
 * @param e - Click event
 * @param href - The URL to navigate to
 */
export const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();

    let target = e.target as HTMLElement;
    while (target && target.tagName !== 'A') {
        target = target.parentElement as HTMLElement;
    }
    console.log(target);
    navigateTo(target.getAttribute('href') ?? '/');
};